import type { Query } from "@tanstack/react-query";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { CreatePaymentBundleRequestBody, PaymentBundleResponse } from "./paymentsApi";
import { createPaymentBundle, deletePaymentBundle, listPaymentBundles, markPaymentBundleAsPaid } from "./paymentsApi";
import { useSnackbar } from "notistack";
import { isListCostsQuery } from "../edit/useCost";

const PAYMENT_BUNDLES_QUERY_KEY = "paymentBundles";

export function isListBundlesQuery({ queryKey }: Query) {
  return queryKey.length > 1 && queryKey[0] === PAYMENT_BUNDLES_QUERY_KEY && typeof queryKey[1] === "boolean";
}

export const usePaymentBundles = (showPaid: boolean) => {
  const { data, isLoading } = useQuery({
    queryKey: [PAYMENT_BUNDLES_QUERY_KEY, showPaid],
    queryFn: ({ signal }) => listPaymentBundles(showPaid, signal),
  });
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const createBundleMutation = useMutation<PaymentBundleResponse, unknown, CreatePaymentBundleRequestBody>({
    mutationFn: (body) => createPaymentBundle(body),
    onSuccess: async (r) => {
      enqueueSnackbar(`Paczka przelewów ${r.name} została utworzona`, {
        variant: "success",
      });
      await queryClient.invalidateQueries({ predicate: isListBundlesQuery });
      await queryClient.invalidateQueries({ predicate: isListCostsQuery });
    },
    onError: async (error) => {
      enqueueSnackbar(`Nie udało się utworzyć paczki przelewów. Powód: ${(error as Error).message}`, {
        variant: "error",
      });
      // The basket may be stale, e.g. its invoices were already bundled by another user
      await queryClient.invalidateQueries({ predicate: isListBundlesQuery });
      await queryClient.invalidateQueries({ predicate: isListCostsQuery });
    },
  });

  const deleteBundleMutation = useMutation<void, unknown, { bundleId: string }>({
    mutationFn: ({ bundleId }) => deletePaymentBundle(bundleId),
    onSuccess: async () => {
      enqueueSnackbar("Paczka przelewów została usunięta", {
        variant: "success",
      });
      await queryClient.invalidateQueries({ predicate: isListBundlesQuery });
      await queryClient.invalidateQueries({ predicate: isListCostsQuery });
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się usunąć paczki przelewów. Powód: ${(error as Error).message}`, {
        variant: "error",
      });
    },
  });

  const markBundleAsPaidMutation = useMutation<void, unknown, { bundleId: string }>({
    mutationFn: ({ bundleId }) => markPaymentBundleAsPaid(bundleId),
    onSuccess: async () => {
      enqueueSnackbar("Paczka przelewów została oznaczona jako opłacona", {
        variant: "success",
      });
      await queryClient.invalidateQueries({ predicate: isListBundlesQuery });
      await queryClient.invalidateQueries({ predicate: isListCostsQuery });
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się oznaczyć paczki przelewów jako opłacona. Powód: ${(error as Error).message}`, {
        variant: "error",
      });
    },
  });

  return {
    paymentBundles: data ?? [],
    isLoading,
    onCreateBundle: (request: CreatePaymentBundleRequestBody) => createBundleMutation.mutateAsync(request),
    onMarkBundleAsPaid: async (bundleId: string) => {
      try {
        await markBundleAsPaidMutation.mutateAsync({ bundleId });
      } catch {
        // Failure is already reported to the user by the mutation's onError
      }
    },
    onDeleteBundle: (bundleId: string) => deleteBundleMutation.mutateAsync({ bundleId }),
  };
};
